import React, { useState, useEffect } from 'react';
import { Modal, Card, Table, Badge, Alert, Spinner, Row, Col, ProgressBar, Button, Form } from 'react-bootstrap';
import { shiftPreferenceAPI } from '../../services';
import { toast } from 'react-toastify';

const DAY_NAMES = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'];
const DAY_LABELS = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

const getColorBadge = (color) => {
  const badges = {
    red: <Badge bg="danger">🔴 Đỏ</Badge>,
    yellow: (
      <Badge
        bg="warning"
        text="dark"
      >
        🟡 Vàng
      </Badge>
    ),
    green: <Badge bg="success">🟢 Xanh</Badge>,
  };
  return badges[color] || <Badge bg="secondary">{color}</Badge>;
};

const getColorClass = (color) => {
  const classes = {
    red: 'bg-danger bg-opacity-10',
    yellow: 'bg-warning bg-opacity-10',
    green: 'bg-success bg-opacity-10',
  };
  return classes[color] || '';
};

const SummaryCard = ({ label, value, icon, color }) => (
  <Card className="h-100 border-0 shadow-sm">
    <Card.Body className="d-flex align-items-center gap-3">
      <div
        className={`rounded-circle p-3 bg-${color} bg-opacity-10`}
        style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <span style={{ fontSize: '24px' }}>{icon}</span>
      </div>
      <div className="flex-grow-1">
        <div className="text-muted small mb-1">{label}</div>
        <div className="h4 mb-0 fw-bold">{value}</div>
      </div>
    </Card.Body>
  </Card>
);

const PreferenceCard = ({ preference, alternativeSuggestions }) => {
  const worstColor = preference.summary?.worst_availability_color || 'green';
  const dayAnalyses = preference.day_analyses || [];

  const getAlternativesForDay = (day) => {
    return alternativeSuggestions?.find((alt) => alt.day === day)?.alternatives || [];
  };

  return (
    <Card
      className={`mb-3 border ${
        worstColor === 'red' ? 'border-danger' : worstColor === 'yellow' ? 'border-warning' : 'border-success'
      }`}
    >
      <Card.Header className={`d-flex justify-content-between align-items-center ${getColorClass(worstColor)}`}>
        <div>
          <h5 className="mb-0">{preference.shift_template?.name || 'N/A'}</h5>
          <small className="text-muted">
            {preference.days_of_week?.map((day) => DAY_NAMES[day] || `Ngày ${day}`).join(', ')}
          </small>
        </div>
        <div>{getColorBadge(worstColor)}</div>
      </Card.Header>
      <Card.Body>
        {dayAnalyses.length > 0 ? (
          <>
            <h6 className="mb-3">📊 Phân tích theo ngày:</h6>
            <Table
              striped
              bordered
              hover
              size="sm"
              className="mb-3"
            >
              <thead>
                <tr>
                  <th>Ngày</th>
                  <th>Đã đăng ký</th>
                  <th>Tỷ lệ</th>
                  <th>Màu</th>
                  <th>Cải thiện</th>
                </tr>
              </thead>
              <tbody>
                {dayAnalyses.map((dayAnalysis, idx) => {
                  const alternatives = getAlternativesForDay(dayAnalysis.day);
                  const bestAlternative = alternatives[0];
                  const improvement = bestAlternative?.improvement_percent || 0;

                  return (
                    <tr
                      key={idx}
                      className={getColorClass(dayAnalysis.availability_color)}
                    >
                      <td className="fw-medium">{dayAnalysis.day_name}</td>
                      <td>
                        {dayAnalysis.other_employees_count} người
                        {dayAnalysis.total_registration_count && (
                          <small className="text-muted d-block">(Tổng: {dayAnalysis.total_registration_count})</small>
                        )}
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <ProgressBar
                            now={dayAnalysis.assignment_probability_percent}
                            variant={
                              dayAnalysis.availability_color === 'red'
                                ? 'danger'
                                : dayAnalysis.availability_color === 'yellow'
                                ? 'warning'
                                : 'success'
                            }
                            style={{ flex: 1, height: '20px' }}
                          />
                          <span className="fw-medium">{dayAnalysis.assignment_probability_percent}%</span>
                        </div>
                      </td>
                      <td>{getColorBadge(dayAnalysis.availability_color)}</td>
                      <td>
                        {improvement > 0 ? (
                          <span className="text-success fw-medium">+{improvement}% ⬆️</span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            {alternativeSuggestions?.some((alt) => alt.alternatives?.length > 0) && (
              <div className="mt-3">
                <h6 className="mb-2">💡 Đề xuất ca thay thế:</h6>
                {alternativeSuggestions
                  .filter((alt) => alt.alternatives?.length > 0)
                  .map((alt, idx) => (
                    <div
                      key={idx}
                      className="mb-2"
                    >
                      <strong className="text-muted">{alt.day_name}:</strong>
                      <ul className="mb-0 mt-1">
                        {alt.alternatives.slice(0, 3).map((alternative, altIdx) => (
                          <li
                            key={altIdx}
                            className="mb-1"
                          >
                            <span className="fw-medium">{alternative.template_name}</span> (
                            {alternative.assignment_probability_percent}% tỷ lệ, +{alternative.improvement_percent}% cải
                            thiện) {getColorBadge(alternative.availability_color)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
              </div>
            )}
          </>
        ) : (
          <div className="text-muted">Không có dữ liệu phân tích</div>
        )}
      </Card.Body>
    </Card>
  );
};

export default function CompetitionAnalysisModal({ show, onHide, employee, branchId, onApprove }) {
  const [loading, setLoading] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [error, setError] = useState(null);
  const [approving, setApproving] = useState(false);
  const [editablePreferences, setEditablePreferences] = useState([]);

  useEffect(() => {
    if (show && employee?.id && branchId) {
      loadAnalysis();
    } else {
      setAnalysisData(null);
      setError(null);
    }
  }, [show, employee?.id, branchId]);

  const loadAnalysis = async () => {
    if (!employee?.id || !branchId) {
      setError('Thiếu thông tin nhân viên hoặc chi nhánh');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await shiftPreferenceAPI.getCompetitionAnalysis(employee.id, branchId);
      const data = response.data || response;

      if (data.preferences && data.preferences.length > 0) {
        setAnalysisData(data);
        setEditablePreferences(
          data.preferences.map((p) => ({
            id: p.id,
            shift_template: p.shift_template,
            daysOfWeekEdited: Array.from(new Set(p.days_of_week || [])).sort(),
            selected: true,
          })),
        );
      } else {
        setAnalysisData(null);
        setEditablePreferences([]);
        setError('Chưa có ca ưu tiên');
      }
    } catch (err) {
      console.error('Error loading competition analysis:', err);
      if (err.status === 404) {
        setError('Chưa có ca ưu tiên');
      } else {
        setError(err.message || 'Không thể tải phân tích cạnh tranh');
      }
      setAnalysisData(null);
      setEditablePreferences([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleDay = (prefId, day) => {
    setEditablePreferences((prev) =>
      prev.map((p) => {
        if (p.id !== prefId) return p;
        const has = p.daysOfWeekEdited.includes(day);
        const days = has ? p.daysOfWeekEdited.filter((d) => d !== day) : [...p.daysOfWeekEdited, day];
        return { ...p, daysOfWeekEdited: days.sort() };
      }),
    );
  };

  const toggleSelect = (prefId) => {
    setEditablePreferences((prev) => prev.map((p) => (p.id === prefId ? { ...p, selected: !p.selected } : p)));
  };

  const handleApprove = async () => {
    try {
      setApproving(true);

      // Duyệt batch ca ưu tiên (theo lựa chọn trong modal) nếu có
      const selectedPrefs = editablePreferences.filter((p) => p.selected);
      if (selectedPrefs.length && branchId) {
        const items = selectedPrefs.map((p) => ({
          id: p.id,
          days_of_week: p.daysOfWeekEdited,
        }));

        await shiftPreferenceAPI.adminReviewBatch({
          branch_id: branchId,
          items,
        });
      }

      // Gọi callback duyệt nhân viên (nếu được truyền vào)
      if (onApprove) {
        await onApprove();
      }

      toast.success('Đã duyệt ca ưu tiên và nhân viên thành công');
      onHide();
    } catch (e) {
      console.error('Approve competition analysis failed', e);
      toast.error(e?.message || 'Duyệt ca ưu tiên thất bại');
    } finally {
      setApproving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title>Phân Tích Mức Độ Cạnh Tranh - {employee?.full_name || employee?.username || 'N/A'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* #090432 — hồ sơ nộp lúc hệ thống gửi mã Zalo hỏng: SĐT CHƯA qua xác thực.
            Cảnh báo ngay trong hộp thoại Duyệt để người duyệt không nhầm là số đã kiểm. */}
        {employee?.phone_verified === false && (
          <div className="alert alert-warning py-2 mb-3">
            <strong>SĐT chưa xác thực.</strong> Hồ sơ này nộp khi hệ thống{' '}
            {employee?.phone_verify_skip_reason === 'sidecar_not_configured'
              ? 'Zalo chưa được cấu hình'
              : 'không gửi được mã Zalo'}
            , nên bước xác thực số điện thoại đã được bỏ qua. Hãy tự liên hệ số{' '}
            <strong>{employee?.phone_number || '(không có)'}</strong> để kiểm tra trước khi duyệt.
          </div>
        )}
        {loading ? (
          <div className="text-center py-5">
            <Spinner
              animation="border"
              role="status"
              className="mb-3"
            >
              <span className="visually-hidden">Đang tải...</span>
            </Spinner>
            <div className="text-muted">Đang tải phân tích...</div>
          </div>
        ) : error ? (
          <Alert
            variant="info"
            className="mb-0"
          >
            <Alert.Heading>Thông báo</Alert.Heading>
            <p className="mb-0">{error}</p>
          </Alert>
        ) : analysisData ? (
          <>
            {editablePreferences.length > 0 && (
              <div className="mb-4">
                <h5 className="mb-3">✅ Chọn ca ưu tiên & ngày trong tuần để duyệt</h5>
                <Card className="mb-3">
                  <Card.Body>
                    <div className="d-flex flex-column gap-3">
                      {editablePreferences.map((p) => (
                        <Row
                          key={p.id}
                          className="align-items-center"
                        >
                          <Col md={4}>
                            <Form.Check
                              type="checkbox"
                              id={`pref-${p.id}`}
                              label={p.shift_template?.name || 'Ca ưu tiên'}
                              checked={p.selected}
                              onChange={() => toggleSelect(p.id)}
                            />
                          </Col>
                          <Col md={8}>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              {DAY_LABELS.map((label, idx) => (
                                <Button
                                  key={label}
                                  size="sm"
                                  variant={p.daysOfWeekEdited.includes(idx) ? 'primary' : 'outline-secondary'}
                                  onClick={() => toggleDay(p.id, idx)}
                                  disabled={!p.selected}
                                  title={DAY_NAMES[idx]}
                                >
                                  {label}
                                </Button>
                              ))}
                              {!p.daysOfWeekEdited.length && p.selected && (
                                <span className="text-danger small ms-2">
                                  Không chọn ngày nào → ca này sẽ bị xoá (reject) khi duyệt
                                </span>
                              )}
                            </div>
                          </Col>
                        </Row>
                      ))}
                    </div>
                  </Card.Body>
                </Card>
              </div>
            )}

            <div className="mb-4">
              <h5 className="mb-3">📊 TỔNG QUAN</h5>
              <Row className="g-3">
                <Col md={3}>
                  <SummaryCard
                    label="Tổng ca"
                    value={analysisData.summary?.total_preferences || 0}
                    icon="📋"
                    color="primary"
                  />
                </Col>
                <Col md={3}>
                  <SummaryCard
                    label="Ca/tuần"
                    value={analysisData.summary?.total_shifts_per_week || 0}
                    icon="📅"
                    color="info"
                  />
                </Col>
                <Col md={3}>
                  <SummaryCard
                    label="Cạnh tranh"
                    value={analysisData.summary?.average_competition_level?.toFixed(1) || 0}
                    icon="⚔️"
                    color={analysisData.summary?.average_competition_level > 5 ? 'danger' : 'warning'}
                  />
                </Col>
                <Col md={3}>
                  <SummaryCard
                    label="Tỷ lệ TB"
                    value={`${analysisData.summary?.average_assignment_probability_percent || 0}%`}
                    icon="📈"
                    color={
                      (analysisData.summary?.average_assignment_probability_percent || 0) < 50
                        ? 'danger'
                        : (analysisData.summary?.average_assignment_probability_percent || 0) < 70
                        ? 'warning'
                        : 'success'
                    }
                  />
                </Col>
              </Row>

              {(analysisData.summary?.average_assignment_probability_percent || 0) < 70 && (
                <Alert
                  variant="warning"
                  className="mt-3"
                >
                  ⚠️ Cảnh báo: Tỷ lệ nhận ca trung bình thấp (&lt; 70%)
                </Alert>
              )}
            </div>

            <div className="mb-4">
              <h5 className="mb-3">📋 DANH SÁCH CA ƯU TIÊN</h5>
              {analysisData.preferences?.map((preference, idx) => (
                <PreferenceCard
                  key={preference.id || idx}
                  preference={preference}
                  alternativeSuggestions={analysisData.alternative_suggestions || []}
                />
              ))}
            </div>

            {analysisData.alternative_suggestions?.some((alt) => alt.alternatives?.length > 0) && (
              <div className="mb-3">
                <h5 className="mb-3">💡 ĐỀ XUẤT TỔNG THỂ</h5>
                <Alert variant="info">
                  Nhân viên này có{' '}
                  {analysisData.preferences?.filter(
                    (p) =>
                      p.summary?.worst_availability_color === 'red' || p.summary?.worst_availability_color === 'yellow',
                  ).length || 0}{' '}
                  ca có tỷ lệ nhận thấp. Xem đề xuất ca thay thế ở trên để cải thiện tỷ lệ nhận ca.
                </Alert>
              </div>
            )}
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onHide}
        >
          Đóng
        </button>
        {/* #024038 — trước đây nút Duyệt CHỈ hiện khi có dữ liệu phân tích ca ưu tiên, nên hồ sơ
            KHÔNG đăng ký ca ưu tiên nào (bước 2 vốn không bắt buộc) thì hộp thoại chỉ có nút Đóng
            → không tài nào duyệt được từ giao diện. Nay hồ sơ không có ca ưu tiên vẫn duyệt được;
            phần duyệt batch ca ưu tiên trong handleApprove vốn đã tự bỏ qua khi danh sách rỗng. */}
        {onApprove && !loading && (
          <button
            type="button"
            className="btn btn-success"
            onClick={handleApprove}
            disabled={approving}
          >
            {approving
              ? 'Đang duyệt...'
              : analysisData && !error
                ? 'Duyệt như hiện tại'
                : 'Duyệt hồ sơ (không có ca ưu tiên)'}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
